import React, { useState, useEffect } from 'react';
import { positions } from '../api';

function Positions({ user }) {
  const [positionsList, setPositionsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [viewingPosition, setViewingPosition] = useState(null);
  const [editingPosition, setEditingPosition] = useState(null);
  const [deletingPosition, setDeletingPosition] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [showAdvancedFilter, setShowAdvancedFilter] = useState(false);
  const [sortField, setSortField] = useState('title');
  const [sortDirection, setSortDirection] = useState('asc');
  const [filters, setFilters] = useState({
    location: '',
    status: '',
    minApplicants: '',
    maxApplicants: '',
    minHires: '',
    maxHires: ''
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(15);
  const [tempFilters, setTempFilters] = useState({
    location: '',
    status: '',
    minApplicants: '',
    maxApplicants: '',
    minHires: '',
    maxHires: ''
  });
  const [formData, setFormData] = useState({
    title: '', 
    description: '', 
    rate: '',
    location: '', 
    lookout_radius: '', 
    visibility: 'External',
    position_status: '',
    work_days: [],
    shift_hours: [],
    changing_schedule: '',
    minimum_education: '',
    age_requirement: '',
    transportation_requirement: '',
    max_applicants: '', 
    required_hires: ''
  });

  useEffect(() => {
    loadPositions();
  }, []);

  const loadPositions = async () => {
    try {
      const response = await positions.getAll();
      setPositionsList(response.data);
    } catch (error) {
      console.error('Error loading positions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (position) => {
    setViewingPosition(position);
    setShowDetailsModal(true);
  };

  const handleEdit = (position) => {
    setEditingPosition(position);
    setFormData({
      title: position.title || '',
      description: position.description || '',
      rate: position.rate || '',
      location: position.location || '',
      lookout_radius: position.lookout_radius || '',
      visibility: position.visibility || 'External',
      position_status: position.position_status || '',
      work_days: position.work_days ? JSON.parse(position.work_days) : [],
      shift_hours: position.shift_hours ? JSON.parse(position.shift_hours) : [],
      changing_schedule: position.changing_schedule || '',
      minimum_education: position.minimum_education || '',
      age_requirement: position.age_requirement || '',
      transportation_requirement: position.transportation_requirement || '',
      max_applicants: position.max_applicants || '',
      required_hires: position.required_hires || ''
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Convert arrays to JSON strings for backend
      const dataToSubmit = {
        ...formData,
        work_days: JSON.stringify(formData.work_days),
        shift_hours: JSON.stringify(formData.shift_hours)
      };
      
      if (editingPosition) {
        await positions.update(editingPosition.id, dataToSubmit);
      } else {
        await positions.create(dataToSubmit);
      }
      
      setShowModal(false);
      setEditingPosition(null);
      setFormData({
        title: '', 
        description: '', 
        rate: '',
        location: '', 
        lookout_radius: '', 
        visibility: 'External',
        position_status: '',
        work_days: [],
        shift_hours: [],
        changing_schedule: '',
        minimum_education: '',
        age_requirement: '',
        transportation_requirement: '',
        max_applicants: '', 
        required_hires: ''
      });
      loadPositions();
    } catch (error) {
      alert(editingPosition ? 'Error updating position' : 'Error creating position');
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingPosition(null);
    setFormData({
      title: '', 
      description: '', 
      requirements: '', 
      rate: '',
      location: '', 
      lookout_radius: '', 
      visibility: 'External',
      position_status: '',
      work_days: [],
      shift_hours: [],
      changing_schedule: '',
      minimum_education: '',
      age_requirement: '',
      transportation_requirement: '',
      max_applicants: '', 
      required_hires: ''
    });
  };

  const handleMultiSelect = (field, value) => {
    const current = formData[field];
    if (current.includes(value)) {
      setFormData({...formData, [field]: current.filter(v => v !== value)});
    } else {
      setFormData({...formData, [field]: [...current, value]});
    }
  };

  const handleDeleteClick = (position) => {
    setDeletingPosition(position);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async (moveToReleased) => {
    try {
      await positions.delete(deletingPosition.id, moveToReleased);
      setShowDeleteModal(false);
      setDeletingPosition(null);
      loadPositions();
    } catch (error) {
      alert('Error deleting position');
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setDeletingPosition(null);
  };

  const getStatusBadge = (status) => {
    const badges = {
      open: 'badge-success',
      closed: 'badge-secondary',
      pending: 'badge-warning',
      filled: 'badge-primary'
    };
    return `badge ${badges[status] || 'badge-secondary'}`;
  };

  // Filter and sort positions
  const getFilteredAndSortedPositions = () => {
    let filtered = [...positionsList];

    // Text search
    if (searchText) {
      filtered = filtered.filter(pos => 
        pos.title.toLowerCase().includes(searchText.toLowerCase())
      );
    }

    // Advanced filters
    if (filters.location) {
      filtered = filtered.filter(pos => pos.location === filters.location);
    }
    if (filters.status) {
      filtered = filtered.filter(pos => pos.status === filters.status);
    }
    if (filters.minApplicants) {
      filtered = filtered.filter(pos => pos.current_applicants >= parseInt(filters.minApplicants));
    }
    if (filters.maxApplicants) {
      filtered = filtered.filter(pos => pos.current_applicants <= parseInt(filters.maxApplicants));
    }
    if (filters.minHires) {
      filtered = filtered.filter(pos => pos.current_hires >= parseInt(filters.minHires));
    }
    if (filters.maxHires) {
      filtered = filtered.filter(pos => pos.current_hires <= parseInt(filters.maxHires));
    }

    // Sort
    filtered.sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      // Handle numeric fields
      if (sortField === 'current_applicants' || sortField === 'current_hires') {
        aVal = parseInt(aVal) || 0;
        bVal = parseInt(bVal) || 0;
      }

      // Handle string fields
      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }

      if (sortDirection === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

    return filtered;
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const clearFilters = () => {
    setSearchText('');
    setFilters({
      location: '',
      status: '',
      minApplicants: '',
      maxApplicants: '',
      minHires: '',
      maxHires: ''
    });
  };

  const handleOpenAdvancedFilter = () => {
    // Copy current filters to temp when opening modal
    setTempFilters({...filters});
    setShowAdvancedFilter(true);
  };

  const handleApplyFilters = () => {
    // Apply temp filters to real filters
    setFilters({...tempFilters});
    setShowAdvancedFilter(false);
  };

  const handleCancelFilters = () => {
    // Discard temp filters
    setShowAdvancedFilter(false);
  };

  const filteredPositions = getFilteredAndSortedPositions();

  // Pagination
  const totalPages = Math.ceil(filteredPositions.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedPositions = filteredPositions.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchText, filters]);

  const getSortIcon = (field) => {
    if (sortField !== field) return ' ⇅';
    return sortDirection === 'asc' ? ' ▲' : ' ▼';
  };

  if (loading) return <div className="spinner"></div>;

  return (
    <div>
      <div className="page-header flex-between">
        <div>
          <h1 className="page-title">Positions</h1>
          <p className="page-subtitle">Manage job positions</p>
        </div>
        {user?.permissions?.edit_positions && (
          <button className="btn btn-primary" onClick={() => setShowModal(true)}>
            + Create Position
          </button>
        )}
      </div>

      {/* Search and Filter Bar */}
      <div className="card" style={{marginBottom: '20px'}}>
        <div style={{display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap'}}>
          <input
            type="text"
            className="form-input"
            placeholder="Search by position title..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{flex: '1', minWidth: '250px'}}
          />
          <button 
            className="btn btn-outline" 
            onClick={handleOpenAdvancedFilter}
          >
            🔍 Advanced Filter
          </button>
          {(searchText || filters.location || filters.status || filters.minApplicants || filters.maxApplicants || filters.minHires || filters.maxHires) && (
            <button 
              className="btn btn-outline" 
              onClick={clearFilters}
              style={{borderColor: '#dc2626', color: '#dc2626'}}
            >
              ✕ Clear Filters
            </button>
          )}
          <div style={{marginLeft: 'auto', color: '#64748b', fontSize: '14px'}}>
            Showing {startIndex + 1}-{Math.min(endIndex, filteredPositions.length)} of {filteredPositions.length}
          </div>
        </div>
      </div>

      <div className="card">
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th onClick={() => handleSort('title')} style={{cursor: 'pointer', userSelect: 'none'}}>
                  Title{getSortIcon('title')}
                </th>
                <th onClick={() => handleSort('location')} style={{cursor: 'pointer', userSelect: 'none'}}>
                  Location{getSortIcon('location')}
                </th>
                <th onClick={() => handleSort('rate')} style={{cursor: 'pointer', userSelect: 'none'}}>
                  Rate{getSortIcon('rate')}
                </th>
                <th onClick={() => handleSort('current_applicants')} style={{cursor: 'pointer', userSelect: 'none'}}>
                  Applicants{getSortIcon('current_applicants')}
                </th>
                <th onClick={() => handleSort('current_hires')} style={{cursor: 'pointer', userSelect: 'none'}}>
                  Hires{getSortIcon('current_hires')}
                </th>
                <th onClick={() => handleSort('status')} style={{cursor: 'pointer', userSelect: 'none'}}>
                  Status{getSortIcon('status')}
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedPositions.map(position => (
                <tr key={position.id} style={{cursor: 'pointer'}}>
                  <td onClick={() => handleViewDetails(position)}><strong>{position.title}</strong></td>
                  <td onClick={() => handleViewDetails(position)}>{position.location || 'N/A'}</td>
                  <td onClick={() => handleViewDetails(position)}>{position.rate || 'N/A'}</td>
                  <td onClick={() => handleViewDetails(position)}>{position.current_applicants} / {position.max_applicants || '∞'}</td>
                  <td onClick={() => handleViewDetails(position)}>{position.current_hires} / {position.required_hires || '∞'}</td>
                  <td onClick={() => handleViewDetails(position)}>
                    <span className={getStatusBadge(position.status)}>
                      {position.status}
                    </span>
                  </td>
                  <td>
                    <div style={{display: 'flex', gap: '8px'}}>
                      {user?.permissions?.edit_positions && (
                        <button 
                          className="btn btn-sm btn-primary"
                          onClick={(e) => { e.stopPropagation(); handleEdit(position); }}
                        >
                          Edit
                        </button>
                      )}
                      {user?.permissions?.delete_positions && (
                        <button 
                          className="btn btn-sm btn-danger"
                          onClick={(e) => { e.stopPropagation(); handleDeleteClick(position); }}
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div style={{
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            gap: '8px', 
            padding: '20px',
            borderTop: '1px solid #e2e8f0'
          }}>
            <button
              className="btn btn-sm btn-outline"
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              style={{opacity: currentPage === 1 ? 0.5 : 1}}
            >
              ⟪ First
            </button>
            <button
              className="btn btn-sm btn-outline"
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              style={{opacity: currentPage === 1 ? 0.5 : 1}}
            >
              ← Prev
            </button>
            
            <div style={{display: 'flex', gap: '4px', alignItems: 'center'}}>
              {[...Array(totalPages)].map((_, idx) => {
                const pageNum = idx + 1;
                // Show first 2, last 2, and pages around current
                if (
                  pageNum === 1 || 
                  pageNum === 2 ||
                  pageNum === totalPages - 1 ||
                  pageNum === totalPages ||
                  (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                ) {
                  return (
                    <button
                      key={pageNum}
                      className={currentPage === pageNum ? "btn btn-sm btn-primary" : "btn btn-sm btn-outline"}
                      onClick={() => setCurrentPage(pageNum)}
                    >
                      {pageNum}
                    </button>
                  );
                } else if (
                  pageNum === currentPage - 2 ||
                  pageNum === currentPage + 2
                ) {
                  return <span key={pageNum}>...</span>;
                }
                return null;
              })}
            </div>

            <button
              className="btn btn-sm btn-outline"
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              style={{opacity: currentPage === totalPages ? 0.5 : 1}}
            >
              Next →
            </button>
            <button
              className="btn btn-sm btn-outline"
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
              style={{opacity: currentPage === totalPages ? 0.5 : 1}}
            >
              Last ⟫
            </button>
          </div>
        )}
      </div>

      {/* Advanced Filter Modal */}
      {showAdvancedFilter && (
        <div className="modal-overlay">
          <div className="modal" style={{maxWidth: '600px'}}>
            <div className="modal-header">
              <h3 className="modal-title">Advanced Filter</h3>
              <button onClick={() => setShowAdvancedFilter(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="grid grid-2" style={{gap: '16px'}}>
                <div className="form-group">
                  <label className="form-label">Location</label>
                  <select
                    className="form-select"
                    value={tempFilters.location}
                    onChange={(e) => setTempFilters({...tempFilters, location: e.target.value})}
                  >
                    <option value="">All Locations</option>
                    <option value="Miami">Miami</option>
                    <option value="Tampa">Tampa</option>
                    <option value="Orlando">Orlando</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Status</label>
                  <select
                    className="form-select"
                    value={tempFilters.status}
                    onChange={(e) => setTempFilters({...tempFilters, status: e.target.value})}
                  >
                    <option value="">All Statuses</option>
                    <option value="open">Open</option>
                    <option value="closed">Closed</option>
                    <option value="pending">Pending</option>
                    <option value="filled">Filled</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Min Applicants</label>
                  <input
                    type="number"
                    className="form-input"
                    placeholder="e.g., 0"
                    value={tempFilters.minApplicants}
                    onChange={(e) => setTempFilters({...tempFilters, minApplicants: e.target.value})}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Max Applicants</label>
                  <input
                    type="number"
                    className="form-input"
                    placeholder="e.g., 100"
                    value={tempFilters.maxApplicants}
                    onChange={(e) => setTempFilters({...tempFilters, maxApplicants: e.target.value})}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Min Hires</label>
                  <input
                    type="number"
                    className="form-input"
                    placeholder="e.g., 0"
                    value={tempFilters.minHires}
                    onChange={(e) => setTempFilters({...tempFilters, minHires: e.target.value})}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Max Hires</label>
                  <input
                    type="number"
                    className="form-input"
                    placeholder="e.g., 50"
                    value={tempFilters.maxHires}
                    onChange={(e) => setTempFilters({...tempFilters, maxHires: e.target.value})}
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="btn btn-outline" 
                onClick={handleCancelFilters}
              >
                Cancel
              </button>
              <button 
                className="btn btn-primary" 
                onClick={handleApplyFilters}
              >
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3 className="modal-title">{editingPosition ? 'Edit Position' : 'Create Position'}</h3>
              <button onClick={handleCloseModal}>×</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body" style={{maxHeight: '70vh', overflowY: 'auto'}}>
                <div className="form-group">
                  <label className="form-label">Title *</label>
                  <input
                    className="form-input"
                    value={formData.title}
                    onChange={e => setFormData({...formData, title: e.target.value})}
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Description</label>
                  <textarea
                    className="form-textarea"
                    value={formData.description}
                    onChange={e => setFormData({...formData, description: e.target.value})}
                    rows="3"
                  />
                </div>

                <div className="grid grid-2">
                  <div className="form-group">
                    <label className="form-label">Location *</label>
                    <select
                      className="form-select"
                      value={formData.location}
                      onChange={e => setFormData({...formData, location: e.target.value})}
                      required
                    >
                      <option value="">Select location</option>
                      <option value="Miami">Miami</option>
                      <option value="Tampa">Tampa</option>
                      <option value="Orlando">Orlando</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Lookout Radius (miles) *</label>
                    <select
                      className="form-select"
                      value={formData.lookout_radius}
                      onChange={e => setFormData({...formData, lookout_radius: e.target.value})}
                      required
                    >
                      <option value="">Select radius</option>
                      <option value="5">5 miles</option>
                      <option value="10">10 miles</option>
                      <option value="25">25 miles</option>
                      <option value="50">50 miles</option>
                      <option value="75">75 miles</option>
                      <option value="100">100 miles</option>
                      <option value="150">150 miles</option>
                      <option value="200">200 miles</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Posting Visibility *</label>
                    <select
                      className="form-select"
                      value={formData.visibility}
                      onChange={e => setFormData({...formData, visibility: e.target.value})}
                      required
                    >
                      <option value="External">External</option>
                      <option value="Internal">Internal</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Position Status</label>
                    <select
                      className="form-select"
                      value={formData.position_status}
                      onChange={e => setFormData({...formData, position_status: e.target.value})}
                    >
                      <option value="">Select status</option>
                      <option value="Full Time">Full Time</option>
                      <option value="Part Time">Part Time</option>
                      <option value="Temporary/Seasonal">Temporary/Seasonal</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Rate</label>
                    <input
                      className="form-input"
                      placeholder="e.g., $15-17/hour"
                      value={formData.rate}
                      onChange={e => setFormData({...formData, rate: e.target.value})}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Work a Changing Schedule?</label>
                    <select
                      className="form-select"
                      value={formData.changing_schedule}
                      onChange={e => setFormData({...formData, changing_schedule: e.target.value})}
                    >
                      <option value="">Select preference</option>
                      <option value="Required">Required</option>
                      <option value="Preferred">Preferred</option>
                      <option value="No Preference">No Preference</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Minimum Education</label>
                    <select
                      className="form-select"
                      value={formData.minimum_education}
                      onChange={e => setFormData({...formData, minimum_education: e.target.value})}
                    >
                      <option value="">Select minimum</option>
                      <option value="No Preference">No Preference</option>
                      <option value="High School or GED">High School or GED</option>
                      <option value="Some College">Some College</option>
                      <option value="Associates">Associates</option>
                      <option value="Bachelors">Bachelors</option>
                      <option value="Masters">Masters</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="form-label">18 years or older?</label>
                    <select
                      className="form-select"
                      value={formData.age_requirement}
                      onChange={e => setFormData({...formData, age_requirement: e.target.value})}
                    >
                      <option value="">Select requirement</option>
                      <option value="Required">Required</option>
                      <option value="Preferred">Preferred</option>
                      <option value="No Preference">No Preference</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Reliable Transportation?</label>
                    <select
                      className="form-select"
                      value={formData.transportation_requirement}
                      onChange={e => setFormData({...formData, transportation_requirement: e.target.value})}
                    >
                      <option value="">Select requirement</option>
                      <option value="Required">Required</option>
                      <option value="Preferred">Preferred</option>
                      <option value="No Preference">No Preference</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Max Applicants</label>
                    <input
                      type="number"
                      className="form-input"
                      value={formData.max_applicants}
                      onChange={e => setFormData({...formData, max_applicants: e.target.value})}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Required Hires</label>
                    <input
                      type="number"
                      className="form-input"
                      value={formData.required_hires}
                      onChange={e => setFormData({...formData, required_hires: e.target.value})}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Work Days</label>
                  <div style={{display: 'flex', flexWrap: 'wrap', gap: '10px', marginTop: '8px'}}>
                    {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday', 'Holidays', 'All'].map(day => (
                      <label key={day} style={{display: 'flex', alignItems: 'center', cursor: 'pointer'}}>
                        <input
                          type="checkbox"
                          checked={formData.work_days.includes(day)}
                          onChange={() => handleMultiSelect('work_days', day)}
                          style={{marginRight: '5px'}}
                        />
                        {day}
                      </label>
                    ))}
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Shift/Hours</label>
                  <div style={{display: 'flex', flexWrap: 'wrap', gap: '10px', marginTop: '8px'}}>
                    {['First/Day', 'Second/Afternoon', 'Third/Nightshift/Hours'].map(shift => (
                      <label key={shift} style={{display: 'flex', alignItems: 'center', cursor: 'pointer'}}>
                        <input
                          type="checkbox"
                          checked={formData.shift_hours.includes(shift)}
                          onChange={() => handleMultiSelect('shift_hours', shift)}
                          style={{marginRight: '5px'}}
                        />
                        {shift}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline" onClick={handleCloseModal}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingPosition ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showDetailsModal && viewingPosition && (
        <div className="modal-overlay">
          <div className="modal" style={{maxWidth: '800px'}}>
            <div className="modal-header">
              <h3 className="modal-title">Position Details</h3>
              <button onClick={() => setShowDetailsModal(false)}>×</button>
            </div>
            <div className="modal-body" style={{maxHeight: '70vh', overflowY: 'auto'}}>
              <div style={{marginBottom: '24px'}}>
                <h2 style={{marginBottom: '8px', color: '#1a202c'}}>{viewingPosition.title}</h2>
                <div style={{display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '16px'}}>
                  <span className="badge badge-primary">{viewingPosition.location}</span>
                  <span className="badge badge-secondary">{viewingPosition.rate}</span>
                  <span className={getStatusBadge(viewingPosition.status)}>{viewingPosition.status}</span>
                  <span className={`badge ${viewingPosition.visibility === 'External' ? 'badge-success' : 'badge-warning'}`}>
                    {viewingPosition.visibility}
                  </span>
                </div>
              </div>

              {viewingPosition.description && (
                <div style={{marginBottom: '20px'}}>
                  <h4 style={{marginBottom: '8px', fontSize: '14px', fontWeight: '600', color: '#4a5568'}}>Description</h4>
                  <p style={{lineHeight: '1.6', color: '#2d3748'}}>{viewingPosition.description}</p>
                </div>
              )}

              <div className="grid grid-2" style={{gap: '16px', marginBottom: '20px'}}>
                <div>
                  <h4 style={{marginBottom: '8px', fontSize: '14px', fontWeight: '600', color: '#4a5568'}}>Lookout Radius</h4>
                  <p>{viewingPosition.lookout_radius ? `${viewingPosition.lookout_radius} miles` : 'N/A'}</p>
                </div>
                <div>
                  <h4 style={{marginBottom: '8px', fontSize: '14px', fontWeight: '600', color: '#4a5568'}}>Position Status</h4>
                  <p>{viewingPosition.position_status || 'N/A'}</p>
                </div>
                <div>
                  <h4 style={{marginBottom: '8px', fontSize: '14px', fontWeight: '600', color: '#4a5568'}}>Work a Changing Schedule?</h4>
                  <p>{viewingPosition.changing_schedule || 'N/A'}</p>
                </div>
                <div>
                  <h4 style={{marginBottom: '8px', fontSize: '14px', fontWeight: '600', color: '#4a5568'}}>Minimum Education</h4>
                  <p>{viewingPosition.minimum_education || 'N/A'}</p>
                </div>
                <div>
                  <h4 style={{marginBottom: '8px', fontSize: '14px', fontWeight: '600', color: '#4a5568'}}>18 years or older?</h4>
                  <p>{viewingPosition.age_requirement || 'N/A'}</p>
                </div>
                <div>
                  <h4 style={{marginBottom: '8px', fontSize: '14px', fontWeight: '600', color: '#4a5568'}}>Reliable Transportation?</h4>
                  <p>{viewingPosition.transportation_requirement || 'N/A'}</p>
                </div>
                <div>
                  <h4 style={{marginBottom: '8px', fontSize: '14px', fontWeight: '600', color: '#4a5568'}}>Max Applicants</h4>
                  <p>{viewingPosition.max_applicants || 'Unlimited'}</p>
                </div>
                <div>
                  <h4 style={{marginBottom: '8px', fontSize: '14px', fontWeight: '600', color: '#4a5568'}}>Required Hires</h4>
                  <p>{viewingPosition.required_hires || 'N/A'}</p>
                </div>
              </div>

              {viewingPosition.work_days && (
                <div style={{marginBottom: '20px'}}>
                  <h4 style={{marginBottom: '8px', fontSize: '14px', fontWeight: '600', color: '#4a5568'}}>Work Days</h4>
                  <div style={{display: 'flex', gap: '8px', flexWrap: 'wrap'}}>
                    {JSON.parse(viewingPosition.work_days).map(day => (
                      <span key={day} className="badge badge-secondary">{day}</span>
                    ))}
                  </div>
                </div>
              )}

              {viewingPosition.shift_hours && (
                <div style={{marginBottom: '20px'}}>
                  <h4 style={{marginBottom: '8px', fontSize: '14px', fontWeight: '600', color: '#4a5568'}}>Shift/Hours</h4>
                  <div style={{display: 'flex', gap: '8px', flexWrap: 'wrap'}}>
                    {JSON.parse(viewingPosition.shift_hours).map(shift => (
                      <span key={shift} className="badge badge-secondary">{shift}</span>
                    ))}
                  </div>
                </div>
              )}

              <div className="grid grid-2" style={{gap: '16px', marginTop: '24px', paddingTop: '24px', borderTop: '1px solid #e2e8f0'}}>
                <div>
                  <h4 style={{marginBottom: '8px', fontSize: '14px', fontWeight: '600', color: '#4a5568'}}>Current Applicants</h4>
                  <p>{viewingPosition.current_applicants || 0}</p>
                </div>
                <div>
                  <h4 style={{marginBottom: '8px', fontSize: '14px', fontWeight: '600', color: '#4a5568'}}>Current Hires</h4>
                  <p>{viewingPosition.current_hires || 0}</p>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setShowDetailsModal(false)}>
                Close
              </button>
              {user?.permissions?.edit_positions && (
                <button 
                  className="btn btn-primary" 
                  onClick={() => {
                    setShowDetailsModal(false);
                    handleEdit(viewingPosition);
                  }}
                >
                  Edit Position
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {showDeleteModal && deletingPosition && (
        <div className="modal-overlay">
          <div className="modal" style={{maxWidth: '500px'}}>
            <div className="modal-header">
              <h3 className="modal-title">Delete Position</h3>
              <button onClick={handleDeleteCancel}>×</button>
            </div>
            <div className="modal-body">
              <p style={{marginBottom: '16px', fontSize: '16px', color: '#2d3748'}}>
                Are you sure you want to delete <strong>{deletingPosition.title}</strong>?
              </p>
              <p style={{marginBottom: '24px', fontSize: '14px', color: '#4a5568'}}>
                This position currently has <strong>{deletingPosition.current_applicants || 0} active applicant(s)</strong>.
              </p>
              <div style={{padding: '16px', backgroundColor: '#FFF8E1', borderRadius: '8px', marginBottom: '16px'}}>
                <p style={{margin: 0, fontSize: '14px', color: '#6D4C41'}}>
                  <strong>What would you like to do with active applicants?</strong>
                </p>
              </div>
            </div>
            <div className="modal-footer" style={{flexDirection: 'column', gap: '12px'}}>
              <button 
                className="btn btn-danger" 
                style={{width: '100%'}}
                onClick={() => handleDeleteConfirm(true)}
              >
                Delete & Move Applicants to Released
              </button>
              <button 
                className="btn btn-outline" 
                style={{width: '100%', borderColor: '#dc2626', color: '#dc2626'}}
                onClick={() => handleDeleteConfirm(false)}
              >
                Delete & Keep Applicants in Active
              </button>
              <button 
                className="btn btn-outline" 
                style={{width: '100%'}}
                onClick={handleDeleteCancel}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Positions;
